import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { verifyKitCode } from '../api/kitAuth'
import { KIT_REVOKED_REASON, kitSuspendedReason } from './deviceCopy'
import {
  clearKitAccess,
  loadKitAccess,
  saveKitAccess,
  toKitAccessClub,
  type KitAccess,
} from './kitAccessStore'

/**
 * El código de kit como AUTENTICACIÓN DEL DISPOSITIVO con el club, paso 1 de
 * las dos superficies compartidas (decisión de mg, 2026-09-07). Este módulo
 * tiene los estados y la regla de cuándo se vuelve a pedir; el espejo del
 * visor es `vr-app/src/ui/KitLogin.tsx`, con la misma tabla.
 *
 *   unbound    no hay binding en este dispositivo (primer arranque, o alguien
 *              borró el storage) → paso 1, sin motivo que explicar.
 *   bound      hay binding y el kit sigue vivo → paso 2, el PIN del jugador.
 *              Es el estado normal: el código NO se vuelve a pedir entre
 *              jugadores ni entre arranques.
 *   revoked    el re-verify de arranque devolvió 404 (kit dado de baja o
 *              reasignado) → paso 1 con el motivo, y el binding se BORRA:
 *              ese código ya no vale para nada.
 *   suspended  el club existe pero tiene el servicio pausado
 *              (`club.service_active === false`) → paso 1 con el motivo, y el
 *              binding se CONSERVA: el kit es válido, lo que venció es el
 *              contrato, y el dispositivo tiene que volver solo cuando el
 *              club se reactiva.
 *
 * Un error de RED en el re-verify no cambia el estado: un club sin wifi tiene
 * que poder seguir tomando PINs con el binding que ya tenía. (En el vr-app
 * esto era un bug: cualquier excepción que no fuera un 429 borraba el binding
 * del visor, así que un Quest sin conexión perdía el kit y pedía el código
 * impreso de nuevo.)
 */

export type KitBindingStatus = 'unbound' | 'bound' | 'revoked' | 'suspended'

export interface KitBindingState {
  status: KitBindingStatus
  /** El binding vigente, o null si no hay ninguno usable. */
  access: KitAccess | null
  /** Qué mostrarle al staff en el paso 1, o null si es un arranque limpio. */
  reason: string | null
}

/** El resultado del re-verify, ya clasificado (sin acoplar el HTTP a la FSM). */
export type KitVerifyOutcome =
  | { kind: 'ok'; access: KitAccess }
  | { kind: 'revoked' }
  | { kind: 'suspended' }
  | { kind: 'offline' }

export function initialBinding(access: KitAccess | null): KitBindingState {
  return access
    ? { status: 'bound', access, reason: null }
    : { status: 'unbound', access: null, reason: null }
}

/**
 * Transición pura del binding ante el resultado de un re-verify. Separada del
 * hook para poder testear la tabla de estados sin router ni red.
 */
export function nextBinding(prev: KitBindingState, outcome: KitVerifyOutcome): KitBindingState {
  switch (outcome.kind) {
    case 'ok':
      return { status: 'bound', access: outcome.access, reason: null }
    case 'revoked':
      return { status: 'revoked', access: null, reason: KIT_REVOKED_REASON }
    case 'suspended':
      return {
        status: 'suspended',
        access: prev.access,
        reason: kitSuspendedReason(prev.access?.club.name ?? null),
      }
    case 'offline':
      return prev
  }
}

/** Clasifica lo que devolvió `/auth/kit/verify` (o lo que tiró) en un outcome. */
export async function verifyBinding(access: KitAccess): Promise<KitVerifyOutcome> {
  try {
    const res = await verifyKitCode(access.code)
    // `service_active` es opcional en el tipo para tolerar un backend viejo:
    // sólo un `false` explícito suspende el dispositivo.
    if (res.club.service_active === false) return { kind: 'suspended' }
    // El re-verify también refresca el branding: un club recién renombrado o
    // con escudo nuevo se ve sin tener que volver a tipear el código.
    return {
      kind: 'ok',
      access: saveKitAccess(access.code, toKitAccessClub(res.club)),
    }
  } catch (e) {
    // 404 = el kit no existe o está inactivo. Cualquier otra cosa (429 del
    // throttling, 5xx, red caída) deja el dispositivo como estaba.
    if (e instanceof ApiError && e.status === 404) return { kind: 'revoked' }
    return { kind: 'offline' }
  }
}

/**
 * Binding del dispositivo con re-verify de arranque. `bind` lo llama el paso 1
 * al verificar un código nuevo; `unbind` es la acción de STAFF ("Desligar
 * esta tablet"), la única que borra un binding sano.
 */
export function useKitBinding(): KitBindingState & {
  bind: (access: KitAccess) => void
  unbind: () => void
} {
  const [state, setState] = useState<KitBindingState>(() => initialBinding(loadKitAccess()))

  // El re-verify corre UNA vez por montaje del dispositivo, no por jugador:
  // es una comprobación del kit, no del login.
  const checked = useRef(false)
  useEffect(() => {
    if (checked.current) return
    checked.current = true
    const access = loadKitAccess()
    if (!access) return
    let cancelled = false
    void (async () => {
      const outcome = await verifyBinding(access)
      if (cancelled) return
      if (outcome.kind === 'revoked') clearKitAccess()
      setState((prev) => nextBinding(prev, outcome))
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const bind = useCallback((access: KitAccess) => {
    setState({ status: 'bound', access, reason: null })
  }, [])

  const unbind = useCallback(() => {
    clearKitAccess()
    setState({ status: 'unbound', access: null, reason: null })
  }, [])

  return { ...state, bind, unbind }
}
